// 구인 공고 상세 페이지
import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import {
  Box,
  Button,
  Flex,
  Grid,
  Text,
  useColorModeValue,
  Badge,
  Stack,
  Heading,
  Divider,
  useToast,
  Alert,
  AlertIcon,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Textarea,
  IconButton,
  Checkbox,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  FormControl,
  FormLabel,
  Input,
} from '@chakra-ui/react';
import { ArrowBackIcon, EditIcon, DeleteIcon, BellIcon } from '@chakra-ui/icons';
import Card from 'components/Card/Card';
import CardHeader from 'components/Card/CardHeader';
import CardBody from 'components/Card/CardBody';
import { 
  getJobPosting, 
  getJobApplications, 
  submitApplication,
  updateApplicationStatus,
  deleteJobPosting,
  getUserProfile,
  createAssignment,
  getUserApplications,
  getUserAssignments,
  getCandidatePool
} from 'utils/firestoreUtils';
import { useAuth } from 'contexts/AuthContext';

const NOTIFICATION_SERVER = process.env.REACT_APP_NOTIFICATION_SERVER || 'https://api.pnucoop.co.kr';
const DEFAULT_TEMPLATE_CODE = 'ppur_2025112616222511178686286';
const TEMPLATE_BODY = `안녕하세요. [*이름*]님
부산대학교 소비자생활협동조합입니다.
희망하신 관련 구직 내용이 등록되어 안내드립니다.

- 공고명: [*1*]
- 위치: [*2*]
- 모집: [*3*]
- 급여: [*4*]
- 기간: [*5*]

[*6*]

감사합니다.`;

const replaceTemplateTokens = (text, replacements) => {
  let result = text;
  Object.entries(replacements).forEach(([key, value]) => {
    result = result.split(`[*${key}*]`).join(value ?? '');
  });
  return result;
};

function JobPostingDetail() {
  const { id } = useParams();
  const history = useHistory();
  const { currentUser, userProfile } = useAuth();
  const toast = useToast();

  const [job, setJob] = useState(null);
  const [applications, setApplications] = useState([]);
  const [applicantsData, setApplicantsData] = useState({});
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [myApplication, setMyApplication] = useState(null);
  const [selectedApplicants, setSelectedApplicants] = useState([]);
  const [selectionNote, setSelectionNote] = useState('');
  const [applicationNote, setApplicationNote] = useState('');
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [applicantHistory, setApplicantHistory] = useState({ applications: [], assignments: [] });
  const [candidatePool, setCandidatePool] = useState([]);
  const [candidateLoading, setCandidateLoading] = useState(false);
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  const [alimtalkFilters, setAlimtalkFilters] = useState({
    all: false,
    matchJobType: true,
    matchDay: false
  });
  const [alimtalkForm, setAlimtalkForm] = useState({
    customNote: ''
  });
  const [alimtalkSending, setAlimtalkSending] = useState(false);
  const [candidateSearch, setCandidateSearch] = useState('');

  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const { isOpen: isSelectOpen, onOpen: onSelectOpen, onClose: onSelectClose } = useDisclosure();
  const { isOpen: isApplicantOpen, onOpen: onApplicantOpen, onClose: onApplicantClose } = useDisclosure();
  const { isOpen: isAlimtalkOpen, onOpen: onAlimtalkOpen, onClose: onAlimtalkClose } = useDisclosure();

  const textColor = useColorModeValue('gray.700', 'white');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const bgColor = useColorModeValue('white', 'gray.700');

  const isAdmin = userProfile?.role === 'admin' || userProfile?.role === 'manager';
  const jobDays = React.useMemo(() => {
    if (!job) return [];

    if (Array.isArray(job?.workDays) && job.workDays.length > 0) {
      return job.workDays;
    }

    const toDay = (dateString) => {
      if (!dateString) return null;
      const parsed = new Date(dateString);
      if (Number.isNaN(parsed.getTime())) return null;
      const days = ['일', '월', '화', '수', '목', '금', '토'];
      return days[parsed.getDay()];
    };

    if (Array.isArray(job?.workDates) && job.workDates.length > 0) {
      return Array.from(new Set(job.workDates.map(toDay).filter(Boolean)));
    }

    if (job?.periodStart) {
      const startDay = toDay(job.periodStart);
      const endDay = toDay(job.periodEnd);
      return Array.from(new Set([startDay, endDay].filter(Boolean)));
    }

    return [];
  }, [job]);
  const jobTemplateValues = React.useMemo(() => ({
    '1': job?.title || '',
    '2': job?.location || '',
    '3': job?.headcount ? `${job.headcount}명` : '',
    '4': job?.wageAmount != null ? `${job.wageType || ''} ${Number(job.wageAmount).toLocaleString()}원`.trim() : '',
    '5': job?.periodStart ? `${job.periodStart} ~ ${job.periodEnd || ''}` : '',
  }), [job]);
  const isSearchMode = candidateSearch.trim().length > 0;
  const messagePreview = React.useMemo(() => {
    return replaceTemplateTokens(TEMPLATE_BODY, {
      이름: '홍길동',
      ...jobTemplateValues,
      '6': alimtalkForm.customNote || '',
    });
  }, [jobTemplateValues, alimtalkForm.customNote]);

  useEffect(() => {
    loadJobDetail();
  }, [id, currentUser]);

  useEffect(() => {
    if (!isAdmin) return;
    loadCandidatePool();
  }, [isAdmin]);

  useEffect(() => {
    if (!job || candidatePool.length === 0) {
      setFilteredCandidates([]);
      setSelectedCandidateIds([]);
      return;
    }
    applyCandidateFilters();
  }, [candidatePool, job, alimtalkFilters]);

  const searchResults = React.useMemo(() => {
    if (!isSearchMode) return [];
    const query = candidateSearch.trim().toLowerCase();
    return filteredCandidates.filter(candidate =>
      candidate.name?.toLowerCase().includes(query)
    );
  }, [candidateSearch, filteredCandidates, isSearchMode]);

  async function loadJobDetail() {
    try {
      setLoading(true);
      const jobData = await getJobPosting(id);
      
      if (!jobData) {
        toast({
          title: '오류',
          description: '공고를 찾을 수 없습니다.',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        history.push('/admin/job-postings');
        return;
      }

      setJob(jobData);

      // 관리자인 경우 신청 목록 조회
      if (isAdmin) {
        const apps = await getJobApplications(id);
        setApplications(apps);

        // 신청자 정보 조회
        const applicantsInfo = {};
        for (const app of apps) {
          const userData = await getUserProfile(app.userId);
          applicantsInfo[app.userId] = userData;
        }
        setApplicantsData(applicantsInfo);
      }

      // 내 신청 여부 확인
      if (currentUser && !isAdmin) {
        const apps = await getJobApplications(id);
        const myApp = apps.find(app => app.userId === currentUser.uid);
        setMyApplication(myApp || null);
      }
    } catch (error) {
      console.error('공고 로딩 오류:', error);
      toast({
        title: '오류',
        description: '공고를 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  }

  async function loadCandidatePool() {
    try {
      setCandidateLoading(true);
      const pool = await getCandidatePool({ activeOnly: true });
      setCandidatePool(pool);
    } catch (error) {
      console.error('인력풀 로딩 오류:', error);
      toast({
        title: '오류',
        description: '인력풀 정보를 불러오지 못했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setCandidateLoading(false);
    }
  }

  function applyCandidateFilters() {
    if (!job) return;
    let candidates = [...candidatePool];

    if (!alimtalkFilters.all) {
      if (alimtalkFilters.matchJobType && job.jobType) {
        candidates = candidates.filter(candidate =>
          candidate.preferredJobs?.includes(job.jobType)
        );
      }

      if (alimtalkFilters.matchDay) {
        if (jobDays.length === 0) {
          candidates = [];
        } else {
          candidates = candidates.filter(candidate =>
            candidate.availableDays?.some(day => jobDays.includes(day))
          );
        }
      }
    }

    setFilteredCandidates(candidates);
    
    // 필터링된 후보자 ID 목록
    const filteredIds = candidates.map(c => c.id);
    
    // 기존 선택된 ID 중에서 현재 필터에 해당하지 않는 ID 제거
    setSelectedCandidateIds(prev => prev.filter(id => filteredIds.includes(id)));

    setFilteredCandidates(candidates);
    setSelectedCandidateIds([]);
  }

  function handleCandidateToggle(candidateId, checked) {
    setSelectedCandidateIds(prev => {
      if (checked) {
        return prev.includes(candidateId) ? prev : [...prev, candidateId];
      }
      return prev.filter(id => id !== candidateId);
    });
  }

  async function handleApply() {
    if (!currentUser) {
      toast({
        title: '로그인 필요',
        description: '로그인 후 지원해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      history.push('/auth/signin');
      return;
    }

    if (!applicationNote.trim()) {
      toast({
        title: '자기소개 필요',
        description: '자기소개를 입력해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      setApplying(true);
      await submitApplication(id, currentUser.uid, applicationNote);
      
      toast({
        title: '지원 완료',
        description: '공고에 성공적으로 지원했습니다.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      setApplicationNote('');
      loadJobDetail(); // 새로고침
    } catch (error) {
      console.error('지원 오류:', error);
      toast({
        title: '오류',
        description: error.message || '지원에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setApplying(false);
    }
  }

  async function handleApplicantClick(app) {
    try {
      const applicant = applicantsData[app.userId];
      setSelectedApplicant({ ...applicant, currentApplication: app });

      // 과거 신청 이력 조회
      const userApps = await getUserApplications(app.userId);
      const userAssignments = await getUserAssignments(app.userId);

      // 각 신청에 대한 공고 정보 가져오기
      const appsWithJobs = await Promise.all(
        userApps.map(async (application) => {
          const jobData = await getJobPosting(application.jobId);
          return { ...application, job: jobData };
        })
      );

      // 각 배정에 대한 공고 정보 가져오기
      const assignmentsWithJobs = await Promise.all(
        userAssignments.map(async (assignment) => {
          const jobData = await getJobPosting(assignment.jobId);
          return { ...assignment, job: jobData };
        })
      );

      setApplicantHistory({
        applications: appsWithJobs,
        assignments: assignmentsWithJobs
      });

      onApplicantOpen();
    } catch (error) {
      console.error('신청자 정보 로딩 오류:', error);
      toast({
        title: '오류',
        description: '신청자 정보를 불러오는데 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }

  async function handleCancelApplication() {
    try {
      await updateApplicationStatus(myApplication.id, 'canceled');
      
      toast({
        title: '취소 완료',
        description: '지원이 취소되었습니다.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      loadJobDetail();
    } catch (error) {
      console.error('취소 오류:', error);
      toast({
        title: '오류',
        description: '취소에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }

  async function handleSelectApplicants() {
    if (selectedApplicants.length === 0) {
      toast({
        title: '선택 필요',
        description: '선정할 신청자를 선택해주세요.',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // 선정된 신청자 처리
      for (const appId of selectedApplicants) {
        const app = applications.find(a => a.id === appId);
        await updateApplicationStatus(appId, 'selected', selectionNote);
        
        // assignment 생성
        await createAssignment(
          id,
          app.userId,
          job.workDates || [job.periodStart],
          currentUser.uid
        );
      }

      // 미선정자 처리
      const notSelected = applications
        .filter(app => !selectedApplicants.includes(app.id) && app.status === 'applied')
        .map(app => app.id);

      for (const appId of notSelected) {
        await updateApplicationStatus(appId, 'rejected', '아쉽게도 이번에는 선정되지 못했습니다.');
      }

      toast({
        title: '선정 완료',
        description: `${selectedApplicants.length}명이 선정되었습니다.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      onSelectClose();
      loadJobDetail();
    } catch (error) {
      console.error('선정 오류:', error);
      toast({
        title: '오류',
        description: '선정 처리에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }

  async function handleSendAlimtalk() {
    const sourceCandidates = isSearchMode ? searchResults : filteredCandidates;
    const recipients = sourceCandidates.filter(
      candidate => selectedCandidateIds.includes(candidate.id) && candidate.phone && candidate.notificationConsent
    );

    // 알림 수신 미동의자 카운트
    const skippedCount = sourceCandidates.filter(
      candidate => selectedCandidateIds.includes(candidate.id) && candidate.phone && !candidate.notificationConsent
    ).length;

    if (recipients.length === 0) {
      const message = skippedCount > 0 
        ? `발송 가능한 대상이 없습니다. (알림 수신 미동의: ${skippedCount}명)`
        : '발송 가능한 대상이 없습니다.';
      
      toast({
        title: '대상 없음',
        description: message,
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    if (skippedCount > 0) {
      toast({
        title: '일부 대상 제외',
        description: `알림 수신 미동의 ${skippedCount}명은 발송에서 제외됩니다.`,
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    }

    const templateCode = DEFAULT_TEMPLATE_CODE;
    setAlimtalkSending(true);
    try {
      for (const candidate of recipients) {
        const phoneNumber = candidate.phone.replace(/\D/g, '');
        const variables = {
          이름: candidate.name || '지원자',
          ...jobTemplateValues,
          '6': alimtalkForm.customNote || '',
        };
        const res = await fetch(`${NOTIFICATION_SERVER}/api/test/send`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            phoneNumber,
            templateCode,
            name: candidate.name || '지원자',
            variables,
          }),
        });
        const data = await res.json();
        if (!data.success) {
          throw new Error(data.error || `${candidate.name || '지원자'}님 발송 실패`);
        }
      }

      toast({
        title: '알림톡 발송 완료',
        description: `${recipients.length}명에게 발송을 요청했습니다.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
      });
      onAlimtalkClose();
    } catch (error) {
      console.error('알림톡 발송 오류:', error);
      toast({
        title: '알림톡 발송 실패',
        description: error.message || '알림톡 발송 중 문제가 발생했습니다.',
        status: 'error',
        duration: 4000,
        isClosable: true,
      });
    } finally {
      setAlimtalkSending(false);
    }
  }

  async function handleDelete() {
    try {
      await deleteJobPosting(id);
      
      toast({
        title: '삭제 완료',
        description: '공고가 삭제되었습니다.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

      history.push('/admin/job-postings');
    } catch (error) {
      console.error('삭제 오류:', error);
      toast({
        title: '오류',
        description: '삭제에 실패했습니다.',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return 'gray';
      case 'open': return 'blue';
      case 'closed': return 'orange';
      case 'selected': return 'green';
      case 'canceled': return 'red';
      default: return 'gray';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'draft': return '작성중';
      case 'open': return '모집중';
      case 'closed': return '마감';
      case 'selected': return '선정완료';
      case 'canceled': return '취소됨';
      default: return status;
    }
  };

  const getAppStatusColor = (status) => {
    switch (status) {
      case 'applied': return 'blue';
      case 'selected': return 'green';
      case 'rejected': return 'red';
      case 'canceled': return 'gray';
      default: return 'gray';
    }
  };

  const getAppStatusText = (status) => {
    switch (status) {
      case 'applied': return '지원완료';
      case 'selected': return '선정';
      case 'rejected': return '미선정';
      case 'canceled': return '취소됨';
      default: return status;
    }
  };

  if (loading) {
    return (
      <Flex direction="column" pt={{ base: '120px', md: '75px' }} align="center">
        <Text>로딩 중...</Text>
      </Flex>
    );
  }

  if (!job) {
    return null;
  }

  return (
    <Flex direction="column" pt={{ base: '120px', md: '75px' }}>
      {/* 헤더 */}
      <Flex justify="space-between" align="center" mb={4}>
        <Button
          leftIcon={<ArrowBackIcon />}
          onClick={() => history.push('/admin/job-postings')}
          variant="ghost"
          size="sm"
        >
          목록으로
        </Button>
        {isAdmin && (
          <Flex gap={2}>
            <Button
              leftIcon={<BellIcon />}
              colorScheme="purple"
              size="sm"
              variant="solid"
              onClick={onAlimtalkOpen}
            >
              알림톡 발송
            </Button>
            <IconButton
              icon={<EditIcon />}
              onClick={() => history.push(`/admin/job-postings/${id}/edit`)}
              aria-label="수정"
              size="sm"
            />
            <IconButton
              icon={<DeleteIcon />}
              colorScheme="red"
              onClick={onDeleteOpen}
              aria-label="삭제"
              size="sm"
            />
          </Flex>
        )}
      </Flex>

      <Grid templateColumns={{ sm: '1fr', lg: '2fr 1fr' }} gap={4} alignItems="start">
        {/* 공고 상세 정보 */}
        <Card>
          <CardBody p={5}>
            <Stack spacing={4}>
              {/* 제목 영역 */}
              <Box>
                <Stack direction="row" spacing={2} mb={2}>
                  <Badge colorScheme={getStatusColor(job.status)}>
                    {getStatusText(job.status)}
                  </Badge>
                  {job.jobType && (
                    <Badge colorScheme="purple" variant="subtle">
                      {job.jobType}
                    </Badge>
                  )}
                </Stack>
                <Heading size="lg" color={textColor} mb={4}>
                  {job.title}
                </Heading>
              </Box>

              {/* 기본 정보 */}
              <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50" _dark={{ bg: 'gray.700' }}>
                <Heading size="sm" mb={3}>📋 공고 정보</Heading>
                <Stack spacing={2} fontSize="sm">
                  <Flex justify="space-between" align="center">
                    <Text color="gray.500" fontWeight="medium">위치</Text>
                    <Text fontWeight="bold">{job.location}</Text>
                  </Flex>
                  {job.jobType && (
                    <Flex justify="space-between" align="center">
                      <Text color="gray.500" fontWeight="medium">업무 유형</Text>
                      <Text fontWeight="bold">{job.jobType}</Text>
                    </Flex>
                  )}
                  <Flex justify="space-between" align="center">
                    <Text color="gray.500" fontWeight="medium">모집인원</Text>
                    <Text fontWeight="bold">{job.headcount}명</Text>
                  </Flex>
                  <Flex justify="space-between" align="center">
                    <Text color="gray.500" fontWeight="medium">급여</Text>
                    <Text fontWeight="bold" color="blue.500">
                      {job.wageType} {job.wageAmount?.toLocaleString()}원
                    </Text>
                  </Flex>
                  <Flex justify="space-between" align="center">
                    <Text color="gray.500" fontWeight="medium" mr={4}>근무기간</Text>
                    <Text fontWeight="bold">
                      {job.periodStart} ~ {job.periodEnd}
                    </Text>
                  </Flex>
                </Stack>
              </Box>

              {/* 상세 설명 */}
              <Box borderWidth="1px" borderRadius="md" p={4}>
                <Heading size="sm" mb={3}>📝 상세 설명</Heading>
                <Text fontSize="sm" lineHeight="1.8" whiteSpace="pre-wrap" color="gray.600" _dark={{ color: 'gray.300' }}>
                  {job.description}
                </Text>
              </Box>

              {/* 자격 요건 */}
              {(job.requiredSkills?.length > 0 || job.preferredSkills?.length > 0) && (
                <Box borderWidth="1px" borderRadius="md" p={4}>
                  <Heading size="sm" mb={3}>✅ 자격 요건</Heading>
                  <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                    {job.requiredSkills?.length > 0 && (
                      <Box>
                        <Text fontWeight="bold" mb={2} fontSize="sm" color="red.500">필수 조건</Text>
                        <Stack spacing={1}>
                          {job.requiredSkills.map((skill, idx) => (
                            <Text key={idx} fontSize="sm">• {skill}</Text>
                          ))}
                        </Stack>
                      </Box>
                    )}
                    {job.preferredSkills?.length > 0 && (
                      <Box>
                        <Text fontWeight="bold" mb={2} fontSize="sm" color="blue.500">우대 사항</Text>
                        <Stack spacing={1}>
                          {job.preferredSkills.map((skill, idx) => (
                            <Text key={idx} fontSize="sm">• {skill}</Text>
                          ))}
                        </Stack>
                      </Box>
                    )}
                  </Grid>
                </Box>
              )}

              {/* 지원 영역 (일반 사용자) */}
              {!isAdmin && job.status === 'open' && (
                <Box borderWidth="1px" borderRadius="md" p={4} bg="blue.50" _dark={{ bg: 'blue.900' }}>
                  {myApplication ? (
                    <>
                      <Alert status={myApplication.status === 'applied' ? 'info' : 'success'} borderRadius="md" mb={3}>
                        <AlertIcon />
                        <Box flex="1">
                          <Text fontWeight="bold" fontSize="sm">
                            {getAppStatusText(myApplication.status)}
                          </Text>
                          <Text fontSize="xs">
                            지원일: {myApplication.appliedAt?.toDate?.()?.toLocaleDateString()}
                          </Text>
                        </Box>
                        {myApplication.status === 'applied' && (
                          <Button
                            size="sm"
                            colorScheme="red"
                            variant="outline"
                            onClick={handleCancelApplication}
                          >
                            지원 취소
                          </Button>
                        )}
                      </Alert>
                      {myApplication.applicationNote && (
                        <Box mt={3} p={3} bg="white" _dark={{ bg: 'gray.700' }} borderRadius="md">
                          <Text fontSize="xs" fontWeight="bold" mb={1}>제출한 자기소개</Text>
                          <Text fontSize="sm" whiteSpace="pre-wrap">{myApplication.applicationNote}</Text>
                        </Box>
                      )}
                    </>
                  ) : (
                    <>
                      <Heading size="sm" mb={3}>💌 지원하기</Heading>
                      <FormControl mb={3}>
                        <FormLabel fontSize="sm">자기소개 *</FormLabel>
                        <Textarea
                          value={applicationNote}
                          onChange={(e) => setApplicationNote(e.target.value)}
                          placeholder="지원 동기와 자기소개를 작성해주세요 (최소 50자)"
                          minH="120px"
                          fontSize="sm"
                          required
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          {applicationNote.length}자
                        </Text>
                      </FormControl>
                      <Button
                        colorScheme="blue"
                        size="lg"
                        w="100%"
                        isLoading={applying}
                        onClick={handleApply}
                        isDisabled={applicationNote.length < 50}
                      >
                        지원하기
                      </Button>
                    </>
                  )}
                </Box>
              )}
            </Stack>
          </CardBody>
        </Card>

        {/* 사이드바 */}
        <Stack spacing={4} position="sticky" top="100px">
          {/* 신청 현황 (관리자) */}
          {isAdmin && (
            <Card>
              <CardBody p={4}>
                <Heading size="sm" mb={3}>📊 신청 현황</Heading>
                <Stack spacing={3}>
                  {/* 통계 박스들 */}
                  <Grid templateColumns="repeat(4, 1fr)" gap={2} ml={4}>
                    <Box textAlign="center" p={3} borderWidth="1px" borderRadius="md" bg="blue.50" _dark={{ bg: 'blue.900' }}>
                      <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }}>전체 신청</Text>
                      <Text fontWeight="bold" fontSize="2xl" color="blue.600">
                        {applications.length}
                      </Text>
                    </Box>
                    <Box textAlign="center" p={3} borderWidth="1px" borderRadius="md" bg="orange.50" _dark={{ bg: 'orange.900' }}>
                      <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }}>대기중</Text>
                      <Text fontWeight="bold" fontSize="2xl" color="orange.600">
                        {applications.filter(a => a.status === 'applied').length}
                      </Text>
                    </Box>
                    <Box textAlign="center" p={3} borderWidth="1px" borderRadius="md" bg="green.50" _dark={{ bg: 'green.900' }}>
                      <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }}>선정</Text>
                      <Text fontWeight="bold" fontSize="2xl" color="green.600">
                        {applications.filter(a => a.status === 'selected').length}
                      </Text>
                    </Box>
                    <Box textAlign="center" p={3} borderWidth="1px" borderRadius="md" bg="red.50" _dark={{ bg: 'red.900' }}>
                      <Text fontSize="xs" color="gray.600" _dark={{ color: 'gray.400' }}>미선정</Text>
                      <Text fontWeight="bold" fontSize="2xl" color="red.600">
                        {applications.filter(a => a.status === 'rejected').length}
                      </Text>
                    </Box>
                  </Grid>

                  {applications.filter(a => a.status === 'applied').length > 0 && (
                    <Button
                      colorScheme="green"
                      size="md"
                      w="100%"
                      onClick={onSelectOpen}
                    >
                      선정 처리
                    </Button>
                  )}
                </Stack>
              </CardBody>
            </Card>
          )}

          {/* 등록 정보 */}
          <Card>
            <CardBody p={4}>
              <Heading size="sm" mb={3}>ℹ️ 등록 정보</Heading>
              <Stack spacing={3} fontSize="sm" ml={4}>
                <Box>
                  <Text color="gray.500" fontSize="xs" mb={1}>등록일</Text>
                  <Text fontWeight="bold">
                    {job.createdAt?.toDate?.()?.toLocaleDateString('ko-KR')}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {job.createdAt?.toDate?.()?.toLocaleTimeString('ko-KR')}
                  </Text>
                </Box>
                <Divider />
                <Box>
                  <Text color="gray.500" fontSize="xs" mb={1}>수정일</Text>
                  <Text fontWeight="bold">
                    {job.updatedAt?.toDate?.()?.toLocaleDateString('ko-KR')}
                  </Text>
                  <Text fontSize="xs" color="gray.500">
                    {job.updatedAt?.toDate?.()?.toLocaleTimeString('ko-KR')}
                  </Text>
                </Box>
                {job.publishedAt && (
                  <>
                    <Divider />
                    <Box>
                      <Text color="gray.500" fontSize="xs" mb={1}>게시일</Text>
                      <Text fontWeight="bold">
                        {job.publishedAt?.toDate?.()?.toLocaleDateString('ko-KR')}
                      </Text>
                    </Box>
                  </>
                )}
                <Divider />
              </Stack>
            </CardBody>
          </Card>
        </Stack>
      </Grid>

      {/* 신청자 목록 (관리자) */}
      {isAdmin && applications.length > 0 && (
        <Card mt={4}>
          <CardBody p={4}>
            <Heading size="md" mb={3}>👥 신청자 목록</Heading>
            <Box overflowX="auto">
              <Table 
                variant="simple" 
                size="sm" 
                ml={4}
                minW="800px"
                width="100%"
              >
                <Thead>
                  <Tr>
                    <Th borderColor={borderColor} fontSize="xs">이름</Th>
                    <Th borderColor={borderColor} fontSize="xs">연락처</Th>
                    <Th borderColor={borderColor} fontSize="xs">소속</Th>
                    <Th borderColor={borderColor} fontSize="xs">지원일</Th>
                    <Th borderColor={borderColor} fontSize="xs">상태</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {applications.map((app) => {
                    const applicant = applicantsData[app.userId];
                    return (
                      <Tr 
                        key={app.id}
                        cursor="pointer"
                        _hover={{ bg: useColorModeValue('gray.50', 'gray.700') }}
                        onClick={() => handleApplicantClick(app)}
                      >
                        <Td borderColor={borderColor} fontSize="sm" fontWeight="medium">
                          {applicant?.name || '-'}
                        </Td>
                        <Td borderColor={borderColor} fontSize="sm">
                          {applicant?.phone || '-'}
                        </Td>
                        <Td borderColor={borderColor} fontSize="sm">
                          {applicant?.userType || '-'}
                        </Td>
                        <Td borderColor={borderColor} fontSize="xs">
                          {app.appliedAt?.toDate?.()?.toLocaleDateString()}
                        </Td>
                        <Td borderColor={borderColor}>
                          <Badge colorScheme={getAppStatusColor(app.status)} fontSize="xs">
                            {getAppStatusText(app.status)}
                          </Badge>
                        </Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
            </Box>
          </CardBody>
        </Card>
      )}

      {/* 알림톡 발송 모달 */}
      <Modal isOpen={isAlimtalkOpen} onClose={onAlimtalkClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>알림톡 발송</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Grid templateColumns={{ base: '1fr', md: '320px 1fr' }} gap={6}>
              <Stack spacing={4}>
                <FormControl isRequired>
                  <FormLabel fontSize="sm">템플릿 코드</FormLabel>
                  <Input value={DEFAULT_TEMPLATE_CODE} isReadOnly />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    고정 템플릿이 사용됩니다.
                  </Text>
                </FormControl>
                <FormControl>
                  <FormLabel fontSize="sm">추가 안내 문구</FormLabel>
                  <Textarea
                    placeholder="예: 자세한 내용은 앱에서 확인해주세요."
                    value={alimtalkForm.customNote}
                    onChange={(e) => setAlimtalkForm({ ...alimtalkForm, customNote: e.target.value })}
                    rows={4}
                  />
                  <Text fontSize="xs" color="gray.500" mt={1}>
                    템플릿의 제일 하단에 삽입됩니다.
                  </Text>
                </FormControl>
                <Box>
                  <Heading size="sm" mb={2}>알림톡 미리보기</Heading>
                  <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50" _dark={{ bg: 'gray.700' }}>
                    <Text fontSize="sm" whiteSpace="pre-wrap" color="gray.700" _dark={{ color: 'gray.100' }}>
                      {messagePreview}
                    </Text>
                  </Box>
                </Box>
              </Stack>
              <Stack spacing={6}>
                <Box>
                  <Heading size="sm" mb={2}>대상 조건</Heading>
                  <Stack spacing={2}>
                    <Checkbox
                      isChecked={alimtalkFilters.all}
                      onChange={(e) => {
                        const { checked } = e.target;
                        setAlimtalkFilters(prev => ({ ...prev, all: checked }));
                      }}
                    >
                      전체 발송
                    </Checkbox>
                    <Checkbox
                      isChecked={alimtalkFilters.matchJobType}
                      onChange={(e) => {
                        const { checked } = e.target;
                        setAlimtalkFilters(prev => ({ ...prev, matchJobType: checked }));
                      }}
                      isDisabled={!job.jobType}
                    >
                      유형이 겹치는 사람 {job.jobType ? `(${job.jobType})` : '(공고에 유형 정보가 없습니다)'}
                    </Checkbox>
                    <Checkbox
                      isChecked={alimtalkFilters.matchDay}
                      onChange={(e) => {
                        const { checked } = e.target;
                        setAlimtalkFilters(prev => ({ ...prev, matchDay: checked }));
                      }}
                      isDisabled={jobDays.length === 0}
                    >
                      요일이 겹치는 사람 {jobDays.length ? `(${jobDays.join(', ')})` : '(근무 요일 정보가 없습니다)'}
                    </Checkbox>
                  </Stack>
                </Box>

                <Box>
                  <Heading size="sm" mb={2}>발송 대상</Heading>
                  <Stack spacing={3}>
                    <FormControl>
                      <FormLabel fontSize="sm">이름 검색</FormLabel>
                      <Input
                        placeholder="이름으로 검색"
                        value={candidateSearch}
                        onChange={(e) => setCandidateSearch(e.target.value)}
                      />
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        검색 시 해당 인원 목록으로 전환됩니다.
                      </Text>
                    </FormControl>
                    {!candidateLoading && !isSearchMode && (
                      <Text fontSize="sm" color="gray.600">
                        조건에 맞는 인원: <Text as="span" fontWeight="bold">{filteredCandidates.length}</Text>명
                      </Text>
                    )}
                    {candidateLoading ? (
                      <Text fontSize="sm" color="gray.500">인력풀을 불러오는 중입니다...</Text>
                    ) : isSearchMode ? (
                      searchResults.length === 0 ? (
                        <Text fontSize="sm" color="gray.500">검색 결과가 없습니다.</Text>
                      ) : (
                        <Box borderWidth="1px" borderRadius="md" maxH="240px" overflowY="auto">
                          <Stack spacing={0} divider={<Divider />}>
                            {searchResults.map(candidate => {
                              const disabled = !candidate.phone || !candidate.notificationConsent;
                              const disabledReason = !candidate.phone 
                                ? '전화번호 없음' 
                                : !candidate.notificationConsent 
                                ? '알림 수신 미동의' 
                                : '';
                              
                              return (
                                <Flex
                                  key={candidate.id}
                                  align="center"
                                  justify="space-between"
                                  px={3}
                                  py={2}
                                  opacity={disabled ? 0.6 : 1}
                                >
                                  <Checkbox
                                    isChecked={selectedCandidateIds.includes(candidate.id)}
                                    onChange={(e) => {
                                      const { checked } = e.target;
                                      handleCandidateToggle(candidate.id, checked);
                                    }}
                                    isDisabled={disabled}
                                  >
                                    <Box>
                                      <Text fontWeight="bold">{candidate.name || '이름 없음'}</Text>
                                      {disabled && disabledReason && (
                                        <Text fontSize="xs" color="red.500">
                                          ({disabledReason})
                                        </Text>
                                      )}
                                    </Box>
                                  </Checkbox>
                                  <Box textAlign="right">
                                    <Text fontSize="sm">{candidate.phone || '전화번호 없음'}</Text>
                                    <Text fontSize="xs" color="gray.500">
                                      {candidate.preferredJobs?.length
                                        ? candidate.preferredJobs.join(', ')
                                        : '희망 업무 미등록'}
                                    </Text>
                                  </Box>
                                </Flex>
                              );
                            })}
                          </Stack>
                        </Box>
                      )
                    ) : null}
                    <Text fontSize="xs" color="gray.500">
                      전화번호가 없는 인원은 자동으로 제외됩니다.
                    </Text>
                  </Stack>
                </Box>
              </Stack>
            </Grid>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAlimtalkClose}>
              취소
            </Button>
            <Button
              colorScheme="purple"
              onClick={handleSendAlimtalk}
              isLoading={alimtalkSending}
              isDisabled={candidateLoading}
            >
              {`발송 (${selectedCandidateIds.length}명)`}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 삭제 확인 모달 */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>공고 삭제</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            정말 이 공고를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onDeleteClose}>
              취소
            </Button>
            <Button colorScheme="red" onClick={handleDelete}>
              삭제
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 선정 처리 모달 */}
      <Modal isOpen={isSelectOpen} onClose={onSelectClose} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>선정 처리</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              <Text fontWeight="bold">선정할 신청자를 선택하세요</Text>
              <Box maxH="300px" overflowY="auto">
                <Stack spacing={2}>
                  {applications
                    .filter(app => app.status === 'applied')
                    .map((app) => {
                      const applicant = applicantsData[app.userId];
                      return (
                        <Checkbox
                          key={app.id}
                          isChecked={selectedApplicants.includes(app.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedApplicants([...selectedApplicants, app.id]);
                            } else {
                              setSelectedApplicants(
                                selectedApplicants.filter(id => id !== app.id)
                              );
                            }
                          }}
                        >
                          <Flex gap={2} align="center">
                            <Text fontWeight="medium">{applicant?.name}</Text>
                            <Text fontSize="sm" color="gray.500">
                              {applicant?.phone}
                            </Text>
                            <Text fontSize="sm" color="gray.500">
                              ({applicant?.userType})
                            </Text>
                          </Flex>
                        </Checkbox>
                      );
                    })}
                </Stack>
              </Box>

              <Box>
                <Text fontWeight="bold" mb={2}>선정 메모 (선택)</Text>
                <Textarea
                  value={selectionNote}
                  onChange={(e) => setSelectionNote(e.target.value)}
                  placeholder="선정 사유나 추가 안내사항을 입력하세요"
                />
              </Box>

              <Alert status="info">
                <AlertIcon />
                선택되지 않은 신청자는 자동으로 미선정 처리됩니다.
              </Alert>
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onSelectClose}>
              취소
            </Button>
            <Button
              colorScheme="green"
              onClick={handleSelectApplicants}
              isDisabled={selectedApplicants.length === 0}
            >
              선정 확정 ({selectedApplicants.length}명)
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* 신청자 상세 정보 모달 */}
      <Modal isOpen={isApplicantOpen} onClose={onApplicantClose} size="4xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {selectedApplicant?.name} 님의 상세 정보
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Tabs colorScheme="blue">
              <TabList>
                <Tab>이번 신청 정보</Tab>
                <Tab>과거 신청 이력</Tab>
                <Tab>근무 이력</Tab>
              </TabList>

              <TabPanels>
                {/* 탭 1: 이번 신청 정보 */}
                <TabPanel>
                  <Stack spacing={4}>
                    {/* 기본 정보 */}
                    <Box borderWidth="1px" borderRadius="md" p={4} bg="gray.50" _dark={{ bg: 'gray.700' }}>
                      <Heading size="sm" mb={3}>👤 신청자 정보</Heading>
                      <Grid templateColumns="repeat(2, 1fr)" gap={3} fontSize="sm">
                        <Box>
                          <Text color="gray.500" mb={1}>이름</Text>
                          <Text fontWeight="bold">{selectedApplicant?.name}</Text>
                        </Box>
                        <Box>
                          <Text color="gray.500" mb={1}>연락처</Text>
                          <Text fontWeight="bold">{selectedApplicant?.phone}</Text>
                        </Box>
                        <Box>
                          <Text color="gray.500" mb={1}>이메일</Text>
                          <Text fontWeight="bold">{selectedApplicant?.email}</Text>
                        </Box>
                        <Box>
                          <Text color="gray.500" mb={1}>소속</Text>
                          <Text fontWeight="bold">{selectedApplicant?.userType}</Text>
                        </Box>
                      </Grid>
                    </Box>

                    {/* 지원 정보 */}
                    <Box>
                      <Heading size="sm" mb={3}>💌 자기소개</Heading>
                      <Box borderWidth="1px" borderRadius="md" p={4} bg="white" _dark={{ bg: 'gray.700' }}>
                        <Text fontSize="sm" lineHeight="1.8" whiteSpace="pre-wrap">
                          {selectedApplicant?.currentApplication?.applicationNote || '작성된 자기소개가 없습니다.'}
                        </Text>
                      </Box>
                    </Box>

                    {/* 지원일 */}
                    <Flex justify="space-between" align="center" fontSize="sm">
                      <Text color="gray.500">지원일시</Text>
                      <Text fontWeight="bold">
                        {selectedApplicant?.currentApplication?.appliedAt?.toDate?.()?.toLocaleString('ko-KR')}
                      </Text>
                    </Flex>
                  </Stack>
                </TabPanel>

                {/* 탭 2: 과거 신청 이력 */}
                <TabPanel>
                  {applicantHistory.applications.length === 0 ? (
                    <Box textAlign="center" py={8}>
                      <Text color="gray.500">과거 신청 이력이 없습니다.</Text>
                    </Box>
                  ) : (
                    <Box overflowX="auto">
                      <Table size="sm" variant="simple">
                        <Thead>
                          <Tr>
                            <Th>공고명</Th>
                            <Th>위치</Th>
                            <Th>지원일</Th>
                            <Th>상태</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {applicantHistory.applications.map((app, idx) => (
                            <Tr key={idx}>
                              <Td fontSize="sm" fontWeight="medium">
                                {app.job?.title || '삭제된 공고'}
                              </Td>
                              <Td fontSize="sm">{app.job?.location || '-'}</Td>
                              <Td fontSize="xs">
                                {app.appliedAt?.toDate?.()?.toLocaleDateString()}
                              </Td>
                              <Td>
                                <Badge colorScheme={getAppStatusColor(app.status)} fontSize="xs">
                                  {getAppStatusText(app.status)}
                                </Badge>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>
                    </Box>
                  )}
                </TabPanel>

                {/* 탭 3: 근무 이력 */}
                <TabPanel>
                  {applicantHistory.assignments.length === 0 ? (
                    <Box textAlign="center" py={8}>
                      <Text color="gray.500">근무 이력이 없습니다.</Text>
                    </Box>
                  ) : (
                    <Box overflowX="auto">
                      <Table size="sm" variant="simple">
                        <Thead>
                          <Tr>
                            <Th>공고명</Th>
                            <Th>위치</Th>
                            <Th>근무일</Th>
                            <Th>확정일</Th>
                            <Th>평점</Th>
                            <Th>상태</Th>
                          </Tr>
                        </Thead>
                        <Tbody>
                          {applicantHistory.assignments.map((assignment, idx) => (
                            <Tr key={idx}>
                              <Td fontSize="sm" fontWeight="medium">
                                {assignment.job?.title || '삭제된 공고'}
                              </Td>
                              <Td fontSize="sm">{assignment.job?.location || '-'}</Td>
                              <Td fontSize="xs">
                                {assignment.workDates?.length > 0
                                  ? assignment.workDates.length > 1
                                    ? `${assignment.workDates[0]} 외 ${assignment.workDates.length - 1}일`
                                    : assignment.workDates[0]
                                  : '-'
                                }
                              </Td>
                              <Td fontSize="xs">
                                {assignment.confirmedAt?.toDate?.()?.toLocaleDateString()}
                              </Td>
                              <Td>
                                {assignment.rating ? (
                                  <Text fontSize="sm" fontWeight="bold" color="yellow.500">
                                    {assignment.rating.toFixed(1)} ⭐
                                  </Text>
                                ) : (
                                  <Text fontSize="sm" color="gray.400">-</Text>
                                )}
                              </Td>
                              <Td>
                                <Badge 
                                  colorScheme={assignment.status === 'completed' ? 'green' : 'blue'} 
                                  fontSize="xs"
                                >
                                  {assignment.status === 'completed' ? '완료' : '진행중'}
                                </Badge>
                              </Td>
                            </Tr>
                          ))}
                        </Tbody>
                      </Table>

                      {/* 평가 코멘트 */}
                      {applicantHistory.assignments.some(a => a.evaluationNote) && (
                        <Box mt={4}>
                          <Heading size="sm" mb={3}>💬 평가 코멘트</Heading>
                          <Stack spacing={2}>
                            {applicantHistory.assignments
                              .filter(a => a.evaluationNote)
                              .map((assignment, idx) => (
                                <Box key={idx} p={3} borderWidth="1px" borderRadius="md" bg="gray.50" _dark={{ bg: 'gray.700' }}>
                                  <Flex justify="space-between" mb={1}>
                                    <Text fontSize="sm" fontWeight="bold">
                                      {assignment.job?.title}
                                    </Text>
                                    <Text fontSize="xs" color="gray.500">
                                      {assignment.confirmedAt?.toDate?.()?.toLocaleDateString()}
                                    </Text>
                                  </Flex>
                                  <Text fontSize="sm" color="gray.600" _dark={{ color: 'gray.300' }}>
                                    {assignment.evaluationNote}
                                  </Text>
                                </Box>
                              ))}
                          </Stack>
                        </Box>
                      )}
                    </Box>
                  )}
                </TabPanel>
              </TabPanels>
            </Tabs>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Flex>
  );
}

export default JobPostingDetail;

